import React, { useState } from 'react'
import { Button, Checkbox, Dropdown, Select } from 'antd'
import { CloudOutlined, DownOutlined, FilterFilled } from '@ant-design/icons'
import styles from './UsersList.module.scss'

export interface FilterOption {
  value: string
  label: string
}

export interface ClientOption {
  id: string
  name: string
}

interface Props {
  activeCount: number
  companyFilter?: string
  onCompanyChange: (v: string | undefined) => void
  clients: ClientOption[]
  clientsLoading: boolean
  roleFilter: string[]
  onRoleChange: (next: string[]) => void
  roleOptions: FilterOption[]
  cognitoRoleFilter: string[]
  onCognitoRoleChange: (next: string[]) => void
  cognitoRoleOptions: FilterOption[]
  syncMatchFilter?: 'diff' | 'same' | 'missing'
  onSyncMatchChange: (v: 'diff' | 'same' | 'missing' | undefined) => void
  statusFilter?: string
  onStatusChange: (v: string | undefined) => void
  onClearAll: () => void
}

// A collapsed trigger button (label + count badge + chevron) that opens a nested
// checkbox menu — matching the design's Roles / Cognito-role fields inside the
// filter panel.
function NestedMultiSelect({
  options,
  value,
  onChange,
  allLabel,
  singularNoun,
  triggerIcon,
  menuHeader,
}: {
  options: FilterOption[]
  value: string[]
  onChange: (next: string[]) => void
  allLabel: string
  singularNoun: string
  triggerIcon?: React.ReactNode
  menuHeader?: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const active = value.length > 0
  const label =
    value.length === 0
      ? allLabel
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ??
          `1 ${singularNoun}`)
        : `${value.length} ${singularNoun}s`

  function toggle(v: string) {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])
  }

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      // Render inside the panel so clicking this nested menu doesn't register as
      // an outside-click that closes the parent filter popover.
      getPopupContainer={(node) => node.parentElement ?? document.body}
      dropdownRender={() => (
        <div className={styles.roleMenu}>
          {menuHeader}
          {options.map((o) => (
            <label key={o.value} className={styles.roleMenuItem}>
              <Checkbox
                checked={value.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              {o.label}
            </label>
          ))}
        </div>
      )}
    >
      <button
        type="button"
        className={`${styles.roleFilterBtn} ${styles.panelTrigger} ${active ? styles.roleFilterBtnActive : ''}`}
      >
        <span className={styles.roleFilterLabel}>
          {triggerIcon}
          {active && (
            <span className={styles.roleFilterBadge}>{value.length}</span>
          )}
          {label}
        </span>
        <DownOutlined style={{ fontSize: 12, color: '#9ca3af' }} />
      </button>
    </Dropdown>
  )
}

export default function UsersFilterPanel({
  activeCount,
  companyFilter,
  onCompanyChange,
  clients,
  clientsLoading,
  roleFilter,
  onRoleChange,
  roleOptions,
  cognitoRoleFilter,
  onCognitoRoleChange,
  cognitoRoleOptions,
  syncMatchFilter,
  onSyncMatchChange,
  statusFilter,
  onStatusChange,
  onClearAll,
}: Props) {
  const [open, setOpen] = useState(false)
  const active = activeCount > 0

  // Keep every field's popup (Selects, nested dropdowns) inside the panel so
  // interacting with them isn't treated as an outside-click that closes it.
  const popupContainer = (node: HTMLElement) =>
    node.parentElement ?? document.body

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      dropdownRender={() => (
        <div className={styles.filterPanel}>
          <div className={styles.panelField}>
            <span className={styles.panelLabel}>Company</span>
            <Select
              showSearch
              placeholder="All companies"
              value={companyFilter}
              onChange={(v) => onCompanyChange(v)}
              allowClear
              onClear={() => onCompanyChange(undefined)}
              loading={clientsLoading}
              disabled={clientsLoading}
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              getPopupContainer={popupContainer}
              style={{ width: '100%' }}
            >
              {clients.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className={styles.panelField}>
            <span className={styles.panelLabel}>Roles</span>
            <NestedMultiSelect
              options={roleOptions}
              value={roleFilter}
              onChange={onRoleChange}
              allLabel="All roles"
              singularNoun="role"
            />
          </div>

          <div className={styles.panelField}>
            <span className={styles.panelLabel}>Cognito role</span>
            <NestedMultiSelect
              options={cognitoRoleOptions}
              value={cognitoRoleFilter}
              onChange={onCognitoRoleChange}
              allLabel="All Cognito roles"
              singularNoun="Cognito role"
              triggerIcon={<CloudOutlined style={{ color: '#9ca3af' }} />}
              menuHeader={
                <div className={styles.roleMenuHeader}>
                  <CloudOutlined />
                  Synced from AWS Cognito
                </div>
              }
            />
          </div>

          <div className={styles.panelField}>
            <span className={styles.panelLabel}>Cognito match</span>
            <Select
              placeholder="All users"
              value={syncMatchFilter}
              onChange={(v) => onSyncMatchChange(v)}
              allowClear
              onClear={() => onSyncMatchChange(undefined)}
              getPopupContainer={popupContainer}
              style={{ width: '100%' }}
            >
              <Select.Option value="diff">Mismatched</Select.Option>
              <Select.Option value="same">Matched</Select.Option>
              <Select.Option value="missing">No Cognito record</Select.Option>
            </Select>
          </div>

          <div className={styles.panelField}>
            <span className={styles.panelLabel}>Status</span>
            <Select
              placeholder="Any status"
              value={statusFilter}
              onChange={(v) => onStatusChange(v)}
              allowClear
              onClear={() => onStatusChange(undefined)}
              getPopupContainer={popupContainer}
              style={{ width: '100%' }}
            >
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
              <Select.Option value="none">None</Select.Option>
            </Select>
          </div>

          <div className={styles.panelFooter}>
            <button
              type="button"
              className={styles.panelClear}
              onClick={onClearAll}
            >
              Clear all
            </button>
            <Button type="primary" onClick={() => setOpen(false)}>
              Done
            </Button>
          </div>
        </div>
      )}
    >
      <button
        type="button"
        className={`${styles.roleFilterBtn} ${active ? styles.roleFilterBtnActive : ''}`}
      >
        <span className={styles.roleFilterLabel}>
          <FilterFilled />
          Filters
          {active && (
            <span className={styles.roleFilterBadge}>{activeCount}</span>
          )}
        </span>
        <DownOutlined style={{ fontSize: 12, color: '#9ca3af' }} />
      </button>
    </Dropdown>
  )
}
